import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
  TextField,
  Typography,
  Button,
  Tooltip,
} from '@mui/material';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import REGEX from '../const/Regex';
import DialogTransition from './Common/DialogTransition';
import { ReactComponent as CloseIcon } from '../assets/CloseIcon.svg';

/**
 * @component ServiceDialog
 * @description ServiceDialog - React component for adding a group.
 * @param {Object} props - The props object containing the component's data and functions.
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {Function} props.setOpen - Function to set the open state of the dialog.
 * @param {Function} props.createGroup - Function to create a new group.
 * @param {string} props.orgName - The organization ID.
 * @param {Function} props.setTriggerGrouplist - Function to trigger group list update.
 * @returns {JSX.Element} React component.
 */

const ServiceDialog = ({ open, setOpen, createService, orgName, product }) => {
  const initialValues = {
    name: '',
  };

  /**
   * Validation schema for the form fields.
   * @typedef {Object} ValidationSchema
   * @property {string} name - The name field validation rules.
   * @property {string} description - The description field validation rules.
   */

  const validationSchema = object({
    name: string()
      .required('Required')
      .min(2, 'Service name must be at least 2 characters')
      .matches(REGEX.DISPLAY_NAME, 'Invalid service name')
      .max(100, 'Service name must be at most 100 characters'),
  });

  /**
   * Formik form object for managing form state and submission.
   * @typedef {Object} FormikForm
   * @property {Object} initialValues - Initial values for form fields.
   * @property {Object} validationSchema - Validation schema for form fields.
   * @property {Function} onSubmit - Function to handle form submission.
   * @property {Function} handleReset - Function to reset form fields.
   */

  const formikForm = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (formData) => {
      const values = {
        name: formData?.name,
        product_id: product,
      };
      createService(values, orgName);
      setOpen(false);
      formikForm.handleReset();
    },
  });

  const handleChange = useCallback(
    (e) => formikForm.handleChange(e),
    [formikForm]
  );

  const handleOnClose = useCallback(() => {
    setOpen((prev) => !prev);
    formikForm.handleReset();
  }, [formikForm, setOpen]);

  return (
    <Dialog
      maxWidth="xs"
      fullWidth={true}
      onClose={handleOnClose}
      TransitionComponent={DialogTransition}
      open={open}
    >
      <DialogTitle>Create Service </DialogTitle>
      <IconButton
        color="primary"
        aria-label="Close dialog"
        onClick={handleOnClose}
        sx={{ position: 'absolute', right: 16, top: 8 }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Service name"
              required
              placeholder="Enter name"
              name="name"
              value={formikForm.values.name}
              onChange={handleChange}
              aria-label="name"
              onBlur={formikForm.handleBlur}
              helperText={
                formikForm?.errors?.name && formikForm?.touched?.name
                  ? formikForm?.errors?.name
                  : null
              }
              error={
                Boolean(formikForm?.errors?.name) && formikForm?.touched?.name
              }
              InputProps={{
                endAdornment: (
                  <Tooltip
                    title={
                      <Typography>
                        - Service name should start with a character. <br />
                        - Numbers (0-9) are allowed. <br />- Underscore(_) and
                        hyphen (-) are allowed. <br />- Spaces are not allowed.
                      </Typography>
                    }
                    arrow
                    placement="top"
                  >
                    <InfoOutlinedIcon />
                  </Tooltip>
                ),
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={formikForm.handleReset}>
          Reset
        </Button>
        <Button variant="contained" onClick={formikForm.handleSubmit}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ServiceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  createService: PropTypes.func.isRequired,
  orgName: PropTypes.string.isRequired,
  product: PropTypes.string.isRequired,
};

export default ServiceDialog;
